// frontend/src/components/Header.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if a path is active
  const isActive = (path) => {
    if (path === '/' || path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname === path;
  };
  
  return (
    <header className="bg-gradient-to-r from-indigo-700 to-purple-700 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Title */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/">
                <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center">
                  <span className="text-lg font-bold text-indigo-700">ZEE</span>
                </div>
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-4 flex items-baseline space-x-4">
                <Link to="/" className="text-white font-bold text-xl">ZEE FAST Analytics</Link>
                <Link 
                  to="/dashboard" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/dashboard') 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-200 hover:text-white'
                  }`}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/reports" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/reports') 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-200 hover:text-white'
                  }`}
                >
                  Reports
                </Link>
                <Link 
                  to="/analytics" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/analytics') 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-200 hover:text-white'
                  }`}
                >
                  Analytics
                </Link>
              </div>
            </div>
          </div>
          
          {/* Demo Tag, Dark Mode Toggle and Profile */}
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              <span className="bg-indigo-800 text-indigo-100 px-3 py-1 rounded-full text-xs font-medium">
                Demo Version
              </span>
              
              <div className="ml-3">
                <DarkModeToggle />
              </div>
              
              <div className="ml-3 relative">
                <div>
                  <button 
                    className="max-w-xs bg-indigo-600 rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-indigo-600 focus:ring-white"
                    id="user-menu"
                    aria-haspopup="true"
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="-mr-2 flex md:hidden">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-indigo-600 inline-flex items-center justify-center p-2 rounded-md text-indigo-200 hover:text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-indigo-600 focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state. */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link 
              to="/dashboard"
              className={`${isActive('/dashboard') ? 'bg-indigo-800 text-white' : 'text-indigo-200 hover:text-white'} block px-3 py-2 rounded-md text-base font-medium`}
            >
              Dashboard
            </Link>
            <Link 
              to="/reports"
              className={`${isActive('/reports') ? 'bg-indigo-800 text-white' : 'text-indigo-200 hover:text-white'} block px-3 py-2 rounded-md text-base font-medium`}
            >
              Reports
            </Link>
            <Link 
              to="/analytics"
              className={`${isActive('/analytics') ? 'bg-indigo-800 text-white' : 'text-indigo-200 hover:text-white'} block px-3 py-2 rounded-md text-base font-medium`}
            >
              Analytics
            </Link>
          </div>
          <div className="pt-4 pb-3 border-t border-indigo-800">
            <div className="flex items-center px-5">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium leading-none text-white">Demo User</div>
                <div className="text-sm font-medium leading-none text-indigo-200">demo@zeefastanalytics.com</div>
              </div>
              <div className="ml-auto">
                <DarkModeToggle />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
